import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, MapPin, Phone, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CONTACT } from "@/lib/constants";
import { API } from "@/lib/api";
import { useIdentity } from "@/context/SiteConfigContext";
import { REQUIRES_CONFIRMATION } from "@/lib/constants";
import Brand from "@/components/Brand";

const REGIONS = ["northAmerica", "europe", "asia", "caribbean", "africa", "oceania"];
const SERVICES = [
  ["incorporation", "/company-formation"],
  ["bankAccounts", "/banks"],
  ["accounting", "/more-services"],
  ["licensing", "/more-services#forex"],
  ["trusts", "/more-services#trust"],
  ["einitin", "/more-services#einitin"],
];
const COMPANY = [
  ["nav.whoWeAre", "/about"],
  ["nav.partnerProgram", "/partnership"],
  ["nav.moreServices", "/more-services"],
  ["common.clientPortal", "/dashboard"],
  ["common.contact", "/contact"],
];

const SocialIco = ({ d }) => (<svg viewBox="0 0 24 24" className="h-4 w-4 fill-current"><path d={d} /></svg>);
const SOCIALS = [
  { label: "LinkedIn", href: "https://www.linkedin.com/company/unicorpltd", d: "M4.98 3.5A2.5 2.5 0 1 1 0 3.5a2.5 2.5 0 0 1 4.98 0zM.5 8h4V24h-4V8zM8 8h3.8v2.2h.1c.5-1 1.8-2.2 3.8-2.2 4 0 4.8 2.6 4.8 6.1V24h-4v-7c0-1.7 0-3.8-2.3-3.8s-2.7 1.8-2.7 3.7V24H8V8z" },
  { label: "Facebook", href: "https://www.facebook.com/unicorpltd/", d: "M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.4v7A10 10 0 0 0 22 12z" },
];

export default function Footer() {
  const id = useIdentity();
  const { t } = useTranslation();
  const [subEmail, setSubEmail] = useState("");
  const [subState, setSubState] = useState("idle");
  const subscribe = async (e) => {
    e.preventDefault();
    setSubState("loading");
    try {
      const r = await fetch(`${API}/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: subEmail.trim() }),
      });
      if (!r.ok) throw new Error("subscribe failed");
      setSubState("done");
    } catch {
      setSubState("error");
    }
  };
  return (
    <footer id="contact" className="border-t border-border bg-primary text-primary-foreground grain" data-testid="footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid gap-10 md:grid-cols-2 lg:grid-cols-12">
        {/* Brand + contact */}
        <div className="lg:col-span-4">
          <Brand size="xl" dark className="brightness-0 invert opacity-90" />
          <p className="mt-5 text-sm text-primary-foreground/70 max-w-sm leading-relaxed">
            {t("footer.blurb")}
          </p>
          <div className="mt-6 flex flex-col gap-2.5 text-sm text-primary-foreground/80">
            <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-2 hover:text-orange-400 transition-colors" data-testid="footer-email"><Mail className="h-4 w-4" /> {CONTACT.email}</a>
            <a href={CONTACT.phoneHref} className="flex items-center gap-2 hover:text-orange-400 transition-colors" data-testid="footer-phone"><Phone className="h-4 w-4" /> {CONTACT.phone}</a>
            <span className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 shrink-0" /> {CONTACT.address}</span>
          </div>
          <div className="mt-6 flex items-center gap-3">
            {SOCIALS.map((s) => (
              <a key={s.label} href={s.href} target="_blank" rel="noreferrer" aria-label={s.label} className="grid place-items-center h-9 w-9 rounded-sm border border-white/15 text-primary-foreground/80 hover:text-orange-400 hover:border-orange-400/40 transition-colors" data-testid={`footer-social-${s.label.toLowerCase()}`}><SocialIco d={s.d} /></a>
            ))}
          </div>

          {/* Newsletter subscribe */}
          <div className="mt-8 max-w-sm">
            <h4 className="text-[11px] font-mono uppercase tracking-widest text-primary-foreground/50">{t("footer.stayUpdated")}</h4>
            <p className="mt-2 text-sm text-primary-foreground/70 leading-relaxed">{t("footer.newsletterBody")}</p>
            {subState === "done" ? (
              <p className="mt-3 text-sm text-orange-400" data-testid="footer-subscribe-success">{t("footer.subscribed")}</p>
            ) : (
              <form onSubmit={subscribe} className="mt-3 flex gap-2">
                <input
                  type="email"
                  required
                  value={subEmail}
                  onChange={(e) => { setSubEmail(e.target.value); if (subState === "error") setSubState("idle"); }}
                  placeholder={t("footer.emailPlaceholder")}
                  aria-label={t("footer.emailAria")}
                  data-testid="footer-subscribe-email"
                  className="flex-1 min-w-0 rounded-sm bg-white/10 border border-white/15 px-3 py-2 text-sm text-primary-foreground placeholder:text-primary-foreground/40 focus:outline-none focus:border-orange-400/60"
                />
                <button
                  type="submit"
                  disabled={subState === "loading"}
                  data-testid="footer-subscribe-btn"
                  className="rounded-sm bg-orange-600 text-white px-4 py-2 text-sm font-medium hover:bg-orange-700 transition-colors disabled:opacity-60"
                >
                  {subState === "loading" ? "…" : t("footer.subscribe")}
                </button>
              </form>
            )}
            {subState === "error" && (
              <p className="mt-2 text-xs text-red-400" data-testid="footer-subscribe-error">{t("footer.subscribeError")}</p>
            )}
          </div>
        </div>

        {/* Company Formation by region */}
        <div className="lg:col-span-3">
          <h4 className="text-[11px] font-mono uppercase tracking-widest text-primary-foreground/50">{t("nav.companyFormation")}</h4>
          <ul className="mt-4 space-y-2.5 text-sm text-primary-foreground/80">
            {REGIONS.map((r) => (
              <li key={r}><Link to="/company-formation" className="hover:text-orange-400 transition-colors" data-testid={`footer-region-${r.toLowerCase()}`}>{t(`footer.region.${r}`)}</Link></li>
            ))}
          </ul>
        </div>

        {/* Services */}
        <div className="lg:col-span-3">
          <h4 className="text-[11px] font-mono uppercase tracking-widest text-primary-foreground/50">{t("footer.services")}</h4>
          <ul className="mt-4 space-y-2.5 text-sm text-primary-foreground/80">
            {SERVICES.map(([k, to]) => (
              <li key={k}><Link to={to} className="hover:text-orange-400 transition-colors">{t(`footer.svc.${k}`)}</Link></li>
            ))}
          </ul>
        </div>

        {/* Company */}
        <div className="lg:col-span-2">
          <h4 className="text-[11px] font-mono uppercase tracking-widest text-primary-foreground/50">{t("footer.company")}</h4>
          <ul className="mt-4 space-y-2.5 text-sm text-primary-foreground/80">
            {COMPANY.map(([k, to]) => (
              <li key={k}><Link to={to} className="hover:text-orange-400 transition-colors">{t(k)}</Link></li>
            ))}
          </ul>
          <Button asChild size="sm" className="mt-5 rounded-sm bg-orange-600 text-white hover:bg-orange-700">
            <Link to="/contact" data-testid="footer-contact-cta">{t("footer.contactUs")} <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
          </Button>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 text-xs text-primary-foreground/60 flex flex-col sm:flex-row justify-between gap-3">
          <div className="space-y-1.5" data-testid="footer-legal">
            <span className="block">© {new Date().getFullYear()} {id.legalName}. {t("footer.legalLine")}</span>
            <span className="block text-primary-foreground/45" data-testid="footer-legal-identity">
              {[
                id.regNumber !== REQUIRES_CONFIRMATION && t("footer.regNo", { v: id.regNumber }),
                id.jurisdiction !== REQUIRES_CONFIRMATION && t("footer.regJurisdiction", { v: id.jurisdiction }),
                t("footer.regOffice", { v: id.office }),
              ].filter(Boolean).join(" · ")} · <a href={`mailto:${id.email}`} className="hover:text-orange-400">{id.email}</a> · <a href={id.website} target="_blank" rel="noreferrer" className="hover:text-orange-400">{id.website.replace(/^https?:\/\//, "")}</a>
            </span>
          </div>
          <div className="flex items-center gap-5 flex-wrap">
            <Link to="/legal/terms" className="hover:text-orange-400 transition-colors" data-testid="footer-terms">{t("footer.terms")}</Link>
            <Link to="/legal/privacy" className="hover:text-orange-400 transition-colors" data-testid="footer-privacy">{t("footer.privacy")}</Link>
            <Link to="/legal/refund" className="hover:text-orange-400 transition-colors" data-testid="footer-refund">{t("footer.refund")}</Link>
            <button onClick={() => window.dispatchEvent(new Event("unicorp:cookie-preferences"))} className="hover:text-orange-400 transition-colors" data-testid="footer-cookie-prefs">{t("footer.cookiePrefs")}</button>
            <Link to="/contact" className="hover:text-orange-400 transition-colors" data-testid="footer-support">{t("footer.support")}</Link>
            <span className="font-mono hidden sm:inline">{t("footer.slogan")}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
